// scio1 parity: subscriptionCoupons.html hardcodes $79 as fallback unit price when price is falsy.
// v1 uses `||` (not `??`) so price === 0 also substitutes 79.
export const DEFAULT_COUPON_UNIT_PRICE = 79;

type CouponLike = {
  name?: string | null;
  percent_off?: number | null;
  amount_off?: number | null;
};

/**
 * Format a Stripe coupon for display in invoice/upcoming-charge tables.
 * Matches Stripe's own dashboard style — `25PTC_Off (25% off)` —
 * to keep our popup labels visually consistent with what admins see in Stripe.
 *
 * Fallback chain:
 *   `name (X% off)` → `name ($X off)` → `name` → `X% off` → `$X off` → ''
 */
export function formatDiscountLabel(coupon?: CouponLike | null): string {
  if (!coupon) return '';
  const name = coupon.name ?? '';
  const percentOff = coupon.percent_off ?? 0;
  const amountOff = coupon.amount_off ?? 0;
  if (name) {
    if (percentOff) return `${name} (${percentOff}% off)`;
    if (amountOff) return `${name} ($${amountOff / 100} off)`;
    return name;
  }
  if (percentOff) return `${percentOff}% off`;
  if (amountOff) return `$${amountOff / 100} off`;
  return '';
}

/**
 * Sum invoice-level discount amounts (in dollars) from Stripe's
 * `total_discount_amounts: [{ amount: cents }]`. Multiple stacked coupons
 * are aggregated into a single number.
 */
export function sumDiscountAmounts(
  totalDiscountAmounts?: Array<{ amount?: number | null }> | null,
): number {
  if (!totalDiscountAmounts?.length) return 0;
  const cents = totalDiscountAmounts.reduce((sum, d) => sum + (d.amount ?? 0), 0);
  return cents / 100;
}

export function computeAmount(couponNumber: number, price?: number | null): string {
  const unit = price || DEFAULT_COUPON_UNIT_PRICE;
  return (couponNumber * unit).toFixed(2);
}

export function computeBalance(
  couponNumber: number,
  couponUsed: number,
  price?: number | null,
): string {
  const unit = price || DEFAULT_COUPON_UNIT_PRICE;
  return (couponNumber * unit - couponUsed * unit).toFixed(2);
}
